import React, { useEffect, useState } from 'react';
import { useHostingStore, HostedServer, HostedServerStatus } from '../../store/hostingStore';
import { useGamingStore } from '../../store/gamingStore';
import { ArrowLeft, Plus, Server, Play, Square, Loader2, Users, HardDrive, MemoryStick } from 'lucide-react';
import CreateServerModal from './CreateServerModal';
import ServerManagement from './ServerManagement';

// ── Status helpers ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<HostedServerStatus, { label: string; color: string; dot: string }> = {
  online:     { label: 'En ligne',     color: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30', dot: 'bg-emerald-400' },
  starting:   { label: 'Démarrage',    color: 'text-yellow-400 bg-yellow-500/15 border-yellow-500/30',   dot: 'bg-yellow-400 animate-pulse' },
  stopping:   { label: 'Arrêt',        color: 'text-orange-400 bg-orange-500/15 border-orange-500/30',   dot: 'bg-orange-400 animate-pulse' },
  offline:    { label: 'Hors ligne',   color: 'text-text-muted bg-bg-tertiary border-border-color',      dot: 'bg-text-muted' },
  installing: { label: 'Installation', color: 'text-blue-400 bg-blue-500/15 border-blue-500/30',         dot: 'bg-blue-400 animate-pulse' },
  suspended:  { label: 'Suspendu',     color: 'text-red-400 bg-red-500/15 border-red-500/30',            dot: 'bg-red-400' },
  hibernated: { label: 'Hiberné',      color: 'text-purple-400 bg-purple-500/15 border-purple-500/30',   dot: 'bg-purple-400' },
};

const SERVER_TYPE_COLORS: Record<string, string> = {
  vanilla: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  paper:   'text-red-400 bg-red-500/10 border-red-500/20',
  spigot:  'text-amber-400 bg-amber-500/10 border-amber-500/20',
  forge:   'text-orange-400 bg-orange-500/10 border-orange-500/20',
  fabric:  'text-blue-400 bg-blue-500/10 border-blue-500/20',
  purpur:  'text-purple-400 bg-purple-500/10 border-purple-500/20',
};

// ── Server Card ─────────────────────────────────────────────────────────────

const ServerCard: React.FC<{
  server: HostedServer;
  onSelect: (server: HostedServer) => void;
  onPower: (id: string, action: 'start' | 'stop') => void;
}> = ({ server, onSelect, onPower }) => {
  const status = STATUS_CONFIG[server.status] || STATUS_CONFIG.offline;
  const typeColor = SERVER_TYPE_COLORS[server.server_type] || SERVER_TYPE_COLORS.vanilla;

  return (
    <div
      onClick={() => onSelect(server)}
      className="group relative bg-bg-secondary border border-border-color rounded-xl overflow-hidden cursor-pointer hover:border-cyan-500/30 transition-all duration-200 hover:shadow-lg hover:shadow-cyan-900/10"
    >
      {/* Top gradient bar */}
      <div className="h-1 bg-gradient-to-r from-cyan-500/60 via-sky-500/40 to-transparent" />

      <div className="p-4">
        {/* Header: name + status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-text-primary truncate">{server.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium rounded-full border ${typeColor}`}>
                {server.server_type}
              </span>
              <span className="text-[10px] text-text-muted">{server.mc_version}</span>
            </div>
          </div>

          {/* Status pill */}
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium rounded-full border ${status.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        </div>

        {/* Info row */}
        <div className="flex items-center gap-3 text-[10px] text-text-muted mb-3">
          <span className="flex items-center gap-1">
            <MemoryStick className="w-3 h-3" />
            {server.memory_mb} MB
          </span>
          <span className="flex items-center gap-1">
            <HardDrive className="w-3 h-3" />
            {(server.disk_mb / 1024).toFixed(1)} GB
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {server.max_players}
          </span>
        </div>

        {/* Quick action */}
        <div className="flex items-center gap-2">
          {server.status === 'offline' || server.status === 'hibernated' ? (
            <button
              onClick={(e) => { e.stopPropagation(); onPower(server.id, 'start'); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/25 transition-colors"
            >
              <Play className="w-3 h-3" />
              Démarrer
            </button>
          ) : server.status === 'online' ? (
            <button
              onClick={(e) => { e.stopPropagation(); onPower(server.id, 'stop'); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition-colors"
            >
              <Square className="w-3 h-3" />
              Arrêter
            </button>
          ) : null}

          {server.subdomain && (
            <span className="ml-auto text-[10px] text-text-muted font-mono">
              {server.subdomain}.mc.erozion.net
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ──────────────────────────────────────────────────────────

const HostingDashboard: React.FC = () => {
  const {
    ownedServers, sharedServers, isLoading,
    fetchServers, fetchPlans, sendPower,
    setCurrentServer,
    _initSocketListeners, _cleanupSocketListeners,
  } = useHostingStore();
  const { goBackToHub } = useGamingStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedServer, setSelectedServer] = useState<HostedServer | null>(null);

  useEffect(() => {
    fetchPlans();
    fetchServers();
    _initSocketListeners();
    return () => _cleanupSocketListeners();
  }, []);

  const handleSelect = (server: HostedServer) => {
    setCurrentServer(server);
    setSelectedServer(server);
  };

  const handlePower = (id: string, action: 'start' | 'stop') => {
    sendPower(id, action);
  };

  // If a server is selected, show its management view
  if (selectedServer) {
    return (
      <ServerManagement
        serverId={selectedServer.id}
        onBack={() => { setSelectedServer(null); setCurrentServer(null); }}
      />
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border-color bg-bg-secondary/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={goBackToHub}
              className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Hub
            </button>
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-cyan-400" />
              <h1 className="text-base font-bold text-text-primary">Erozion Go</h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-500 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouveau serveur
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto gaming-scrollbar">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          ) : ownedServers.length === 0 && sharedServers.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
                <Server className="w-8 h-8 text-cyan-400" />
              </div>
              <h2 className="text-lg font-bold text-text-primary mb-1">Aucun serveur</h2>
              <p className="text-sm text-text-muted mb-4">Créez votre premier serveur Minecraft hébergé gratuitement.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-500 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Créer un serveur
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Owned servers */}
              {ownedServers.length > 0 && (
                <div>
                  <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                    Mes serveurs
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ownedServers.map((server) => (
                      <ServerCard key={server.id} server={server} onSelect={handleSelect} onPower={handlePower} />
                    ))}
                    {/* Add card */}
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="flex flex-col items-center justify-center min-h-[160px] border-2 border-dashed border-border-color rounded-xl hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-all duration-200 group"
                    >
                      <Plus className="w-8 h-8 text-text-muted group-hover:text-cyan-400 transition-colors" />
                      <span className="text-xs text-text-muted group-hover:text-text-secondary mt-2 transition-colors">
                        Nouveau serveur
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Shared servers */}
              {sharedServers.length > 0 && (
                <div>
                  <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                    Serveurs partagés
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sharedServers.map((server) => (
                      <ServerCard key={server.id} server={server} onSelect={handleSelect} onPower={handlePower} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <CreateServerModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
};

export default HostingDashboard;
